/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */
import React from 'react';
import {useSelector, useStore, useDispatch} from "react-redux";
import {Button} from "@carbon/react";
import {
    selectDisableButtons,
    selectCurrentPicklistId,
    selectViewData,
} from "../redux/formula_search_redux";
import {
    makeSetSearchTextAction,
    makeClearSelectedItemsAction,
} from "mdx-ivcomp-common-ui/dist/src/components/ivpicklist/iv-picklist-redux/iv-picklist-redux";
import {useHistory} from "react-router-dom";
import {useCallback} from "react";
import {    FORMULA_NUTRITION_DETAILS_ROUTE,
    FORMULA_PRODUCT_NOTES_ROUTE,
    FORMULA_SEARCH_PANEL_NAME, NEOFAX_FORMULA_PICKLIST_ID,
    RESULT_TYPE_PEDIATRICS, RESULT_TYPE_NEOFAX} from "../../../../common/constants";
import { trackAmplitude } from "../../../../../../utils";
import { NEOFAX_PEDIATRICS_CONTENT_SET_ID } from "../../../../../../utils/commonUtils";


const FormulaViewButtons = () => {
    // these references should never change and trigger a re-render
    const redux_store = useStore(); // avoid using useSelector for selected_items to prevent re-rendering
    const dispatch = useDispatch();
    const history = useHistory();

    // only reason to rerender buttons is if they transiton from enabled to disabled or vice versa
    const is_button_disabled = useSelector(selectDisableButtons);

    const onClearButtonClickHandler = useCallback(() => {
        const picklistId = selectCurrentPicklistId(redux_store.getState());
        dispatch(makeClearSelectedItemsAction(picklistId, FORMULA_SEARCH_PANEL_NAME));
        dispatch(makeSetSearchTextAction("", picklistId, FORMULA_SEARCH_PANEL_NAME));
    },[dispatch, redux_store]);

    const onViewNutritionButtonClickHandler = useCallback( () => {
        const global_state = redux_store.getState();
        const { picklistId, display_choice, selected_formula_ids, selected_formula_names } = selectViewData(global_state);
        // http://localhost:3000/micromedex3#/enteral/nutrientDetails?docIds=66,128,136&displayFormat=per100Cal&resultType=neofax
        if (selected_formula_ids.length === 0) {
            return;
        }
        const encoded_doc_ids = encodeURI(selected_formula_ids);
        const formulaType = picklistId === NEOFAX_FORMULA_PICKLIST_ID ? RESULT_TYPE_NEOFAX : RESULT_TYPE_PEDIATRICS;
        const searchParams = `?docIds=${encoded_doc_ids}&displayFormat=${display_choice}&resultType=${formulaType}`

        const formulaNameProperty = picklistId === NEOFAX_FORMULA_PICKLIST_ID ? "neonatalFormulaNames" : "pediatricFormulaNames";
        const formulaName = selected_formula_names.join("|");;

        trackAmplitude(
            {
                searchType: "Neo Peds Search",
                actionType: "SearchAndDocRetrieval",
                [formulaNameProperty]: formulaName
            },
            NEOFAX_PEDIATRICS_CONTENT_SET_ID
        );
        console.log('history.push:', `/#${FORMULA_NUTRITION_DETAILS_ROUTE}${searchParams}`);
        history.push({
            pathname: FORMULA_NUTRITION_DETAILS_ROUTE,
            // and parameters like docId, neofax or pediatrics  and displayFormat
            search: searchParams
        });
    },[redux_store, history]);

    const onViewProductsButtonClickHandler = useCallback( () => {
        const global_state = redux_store.getState();
        const { picklistId, selected_formula_ids, selected_formula_names } = selectViewData(global_state);
        if (selected_formula_ids.length === 0) {
            return;
        }
        // http://localhost:3000/micromedex3#/enteral/productnotes?resultType=neonatal&docIds=66,77
        const encoded_doc_ids = encodeURI(selected_formula_ids);
        const formulaType = picklistId === NEOFAX_FORMULA_PICKLIST_ID ? RESULT_TYPE_NEOFAX : RESULT_TYPE_PEDIATRICS;
        const searchParams = `?docIds=${encoded_doc_ids}&resultType=${formulaType}`;

        const formulaNameProperty = picklistId === NEOFAX_FORMULA_PICKLIST_ID ? "neonatalFormulaNames" : "pediatricFormulaNames";
        const formulaName = selected_formula_names.join("|");

        trackAmplitude(
            {
                searchType: "Neo Peds Search",
                actionType: "SearchAndDocRetrieval",
                [formulaNameProperty]: formulaName
            },
            NEOFAX_PEDIATRICS_CONTENT_SET_ID
        );
        console.log('history.push:', `/#${FORMULA_PRODUCT_NOTES_ROUTE}${searchParams}`);
        history.push({
            pathname: FORMULA_PRODUCT_NOTES_ROUTE,
            // and parameters like docId, neofax or pediatrics  and displayFormat
            search: encodeURI(searchParams)
        });
    },[redux_store, history]);

    return <div className="formula-view-buttons">
        <Button
            className="primary-button bottom-view"
            kind="primary"
            id="view-nutrient-details"
            disabled={is_button_disabled}
            onClick={onViewNutritionButtonClickHandler}
        >
            View Nutrient Details
        </Button>
        <Button
            className="secondary-button bottom-view"
            kind="secondary"
            id="view-product-notes"
            disabled={is_button_disabled}
            onClick={onViewProductsButtonClickHandler}
        >
            View Product Notes
        </Button>
        <Button
            className="tertiary-button bottom-clear"
            kind="tertiary"
            id="clear-formula-button"
            disabled={is_button_disabled}
            onClick={onClearButtonClickHandler}
        >
            Clear All
        </Button>
    </div>
}

export default React.memo(FormulaViewButtons);